"use client"

import {
  Tooltip as RadixTooltip,
  TooltipArrow,
  TooltipContent,
  TooltipContentProps,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@radix-ui/react-tooltip"

import { Button, Text } from "@/elements"
import { ButtonProps } from "@/elements/Button/Button"
import clsx from "clsx"
import { forwardRef, useRef, useState } from "react"

// Create a container ref that will be used as the tooltip's container
const TooltipContainer = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => (
  <div ref={ref} style={{ position: "relative" }} {...props} />
))
TooltipContainer.displayName = "TooltipContainer"

type Props = {
  trigger: React.ReactNode
  width: number
  text?: string
  button?: ButtonProps
  contentProps?: TooltipContentProps
  textClassName?: string
  children?: React.ReactNode
}

export const Tooltip: React.FC<Props> = ({
  children,
  trigger,
  width = 274,
  text,
  button,
  contentProps,
  textClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  return (
    <TooltipContainer ref={containerRef}>
      <TooltipProvider delayDuration={100}>
        <RadixTooltip open={open}>
          <TooltipTrigger
            onMouseEnter={() => {
              setOpen(true)
            }}
            onMouseLeave={() => {
              setOpen(false)
            }}
            onFocus={() => {
              setOpen(true)
            }}
            onBlur={() => {
              setOpen(false)
            }}
            asChild>
            {trigger}
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              sideOffset={5}
              className="z-[500] rounded-[6px] bg-bg-1"
              style={{
                width: `${width}px`,
                boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.10)",
              }}
              {...contentProps}>
              <div className="flex flex-col gap-y-4 p-4">
                {children ? (
                  children
                ) : (
                  <>
                    <Text variant="body2" className={clsx("text-center", textClassName)}>
                      {text}
                    </Text>
                    {button ? <Button {...button} /> : null}
                  </>
                )}
              </div>
              <TooltipArrow className="fill-bg-1" />
            </TooltipContent>
          </TooltipPortal>
        </RadixTooltip>
      </TooltipProvider>
    </TooltipContainer>
  )
}
